import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getVirtualTenantAlarmPaths, listVirtualTenants, getSite } from '../../services/siteManagement/siteManagement.service';
import { createRule, deleteRule, getRule, listSiteRules, patchRuleStatus, updateRule } from '../../services/ruleManagement/ruleManagement.service';
import { formatDateTime } from '../../helpers/formatDateTime';
import { DateTimeDisplay, Breadcrumbs, ActionList, StatusMessage } from "../../components/ui";
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import Modal from '../../components/ui/Modal';
import { ModalContent } from '../../components/ui/ModalContent';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import VirtualTenantsForm from '../../components/forms/VirtualTenantsForm';
import RulesForm, { type RuleFormData } from '../../components/forms/RulesForm';
import type { VirtualTenantDetail } from '../../services/siteManagement/siteManagement.types';
import type { CreateRuleRequest, RuleSummary } from '../../services/ruleManagement/ruleManagement.types';

interface AlarmPathAssociation {
  id?: number;
  virtualTenantId?: number;
  alarmPathId?: number;
  pathToAlarm?: string;
  siteIdentifier?: string;
  isActive?: boolean;
  inheritFromParent?: boolean;
  priority?: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

const RULE_TO_VIRTUAL_TENANT_MAPPING_STORAGE_KEY = 'rules:ruleToVirtualTenantMapping';

const loadRuleToVirtualTenantMapping = (): Map<number, string[]> => {
  try {
    const raw = localStorage.getItem(RULE_TO_VIRTUAL_TENANT_MAPPING_STORAGE_KEY);
    if (!raw) {
      return new Map<number, string[]>();
    }
    const parsed = JSON.parse(raw) as Array<[number, string[]]>;
    return new Map<number, string[]>(
      parsed.map(([ruleId, virtualTenantIds]) => [
        Number(ruleId),
        Array.isArray(virtualTenantIds) ? virtualTenantIds.map(String) : []
      ])
    );
  } catch (_error) {
    return new Map<number, string[]>();
  }
};

const saveRuleToVirtualTenantMapping = (ruleId: number, virtualTenantIds: string[]) => {
  const next = loadRuleToVirtualTenantMapping();
  if (virtualTenantIds.length > 0) {
    next.set(ruleId, virtualTenantIds.map(String));
  } else {
    next.delete(ruleId);
  }
  localStorage.setItem(
    RULE_TO_VIRTUAL_TENANT_MAPPING_STORAGE_KEY,
    JSON.stringify(Array.from(next.entries()))
  );
};

const persistRuleToVirtualTenantMapping = (mapping: Map<number, string[]>) => {
  localStorage.setItem(
    RULE_TO_VIRTUAL_TENANT_MAPPING_STORAGE_KEY,
    JSON.stringify(Array.from(mapping.entries()))
  );
};

const deleteRuleToVirtualTenantMapping = (ruleId: number) => {
  const next = loadRuleToVirtualTenantMapping();
  next.delete(ruleId);
  localStorage.setItem(
    RULE_TO_VIRTUAL_TENANT_MAPPING_STORAGE_KEY,
    JSON.stringify(Array.from(next.entries()))
  );
};

const extractRuleAlarmPathIds = (alarmPaths: unknown): number[] => {
  if (!Array.isArray(alarmPaths)) {
    return [];
  }

  const ids: number[] = [];
  alarmPaths.forEach((item: unknown) => {
    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>;
      const maybeId = obj.id ?? obj.alarmPathId;
      const numericId = Number(maybeId);
      if (!Number.isNaN(numericId) && numericId > 0) {
        ids.push(numericId);
      }
    }
  });

  return ids;
};

const extractRuleAlarmPathLabelsById = (alarmPaths: unknown): Record<string, string> => {
  if (!Array.isArray(alarmPaths)) {
    return {};
  }

  const labelsById: Record<string, string> = {};
  alarmPaths.forEach((item: unknown) => {
    if (typeof item === 'object' && item !== null) {
      const obj = item as Record<string, unknown>;
      const maybeId = obj.id ?? obj.alarmPathId;
      const numericId = Number(maybeId);
      const pathToAlarm = typeof obj.pathToAlarm === 'string' ? obj.pathToAlarm : '';
      if (!Number.isNaN(numericId) && numericId > 0 && pathToAlarm) {
        labelsById[String(numericId)] = pathToAlarm;
      }
    }
  });

  return labelsById;
};

const VirtualTenantsDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [virtualTenant, setVirtualTenant] = useState<VirtualTenantDetail | null>(null);
  const [alarmPaths, setAlarmPaths] = useState<AlarmPathAssociation[] | null>(null);
  const [parentSiteRules, setParentSiteRules] = useState<RuleSummary[] | null>(null);
  const [virtualTenantRules, setVirtualTenantRules] = useState<RuleSummary[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingVT, setEditingVT] = useState<any>(null);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editingRuleData, setEditingRuleData] = useState<RuleFormData | null>(null);
  const [isEditRuleModalOpen, setIsEditRuleModalOpen] = useState<boolean>(false);
  const [isEditFormValid, setIsEditFormValid] = useState<boolean>(true);
  const [showEditErrorSummary, setShowEditErrorSummary] = useState<boolean>(false);
  const [newRules, setNewRules] = useState<RuleFormData[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isCreateFormValid, setIsCreateFormValid] = useState<boolean>(true);
  const [showCreateErrorSummary, setShowCreateErrorSummary] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('Confirm');
  const confirmResolverRef = useRef<((confirmed: boolean) => void) | undefined>(undefined);
  const confirmModalHeaderClass = /(delete|deactivate)/i.test(confirmModalTitle)
    ? 'modal__header--danger'
    : undefined;

  const showConfirmModal = (message: string, title = 'Confirm'): Promise<boolean> => {
    setConfirmModalMessage(message);
    setConfirmModalTitle(title);
    setConfirmModalOpen(true);
    return new Promise(resolve => {
      confirmResolverRef.current = resolve;
    });
  };

  const parseRuleFormPathIds = (val?: string): number[] => {
    if (!val) { return []; }
    return val.split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n) && n > 0);
  };

  // Without explicit paths the backend matches every path on the site, so fall back to the VT's own paths.
  const resolveVirtualTenantRulePathIds = (val?: string): number[] => {
    const selected = parseRuleFormPathIds(val);
    if (selected.length > 0) {
      return selected;
    }
    return (alarmPaths || [])
      .map(p => Number(p.alarmPathId ?? p.id))
      .filter(n => !Number.isNaN(n) && n > 0);
  };

  const mapRuleDetailToFormData = (rule: any, parentSiteRegistryId?: number): RuleFormData => ({
    ruleName: rule.ruleName || '',
    isActive: rule.isActive ?? true,
    isUltraCritical: rule.isUltraCritical ?? false,
    bindPathPriority: rule.bindPathPriority ?? true,
    distributionGroupIds: (rule.distributionGroups || []).map((dg: any) => String(dg.distributionGroupId)).join(','),
    attachmentGroupIds: (rule.attachmentGroups || []).map((ag: any) => String(ag.attachmentGroupId)).join(','),
    appliesToSiteIds: parentSiteRegistryId ? String(parentSiteRegistryId) : '',
    appliesToVirtualTenantIds: '',
    filteringPaths: extractRuleAlarmPathIds(rule.alarmPaths).join(','),
    filteringPathLabelsById: extractRuleAlarmPathLabelsById(rule.alarmPaths),
    filteringPriorities: rule.priorities || [],
    hideRtn: rule.hideRtn ?? false,
    rtnExceptions: (rule.rtnExceptions || []).join(',')
  });

  const resolveVirtualTenantRules = async (
    vt: VirtualTenantDetail,
    vtPathItems: AlarmPathAssociation[],
  ): Promise<{ parentRules: RuleSummary[]; vtRules: RuleSummary[] }> => {
    const parentSiteRegistryId = vt.siteIdentifierRegistryId || vt.parentId || vt.parentSiteHierarchyId;
    if (!parentSiteRegistryId) {
      return { parentRules: [], vtRules: [] };
    }

    const rulesRes = await listSiteRules(parentSiteRegistryId);
    if (!rulesRes?.success || !rulesRes.data) {
      return { parentRules: [], vtRules: [] };
    }

    const siteRules = rulesRes.data.siteRules || [];
    const ruleToVirtualTenantMapping = loadRuleToVirtualTenantMapping();
    let mappingChanged = false;
    (rulesRes.data.virtualTenants || []).forEach(v => {
      const candidateVirtualTenantId = Number(v.siteHierarchyId);
      if (Number.isNaN(candidateVirtualTenantId) || !Array.isArray(v.rules)) {
        return;
      }
      v.rules.forEach(rule => {
        const ruleId = Number(rule.siteRuleId);
        if (Number.isNaN(ruleId)) {
          return;
        }
        const nextVirtualTenantIds = [String(candidateVirtualTenantId)];
        const existingVirtualTenantIds = ruleToVirtualTenantMapping.get(ruleId) || [];
        if (existingVirtualTenantIds.length !== 1 || existingVirtualTenantIds[0] !== nextVirtualTenantIds[0]) {
          ruleToVirtualTenantMapping.set(ruleId, nextVirtualTenantIds);
          mappingChanged = true;
        }
      });
    });
    if (mappingChanged) {
      persistRuleToVirtualTenantMapping(ruleToVirtualTenantMapping);
    }

    const inheritedParentRules = siteRules.filter(rule => {
      const mappedVirtualTenantIds = ruleToVirtualTenantMapping.get(Number(rule.siteRuleId)) || [];
      return mappedVirtualTenantIds.length === 0;
    });
    const vtContainer = (rulesRes.data.virtualTenants || []).find(v => {
      const candidateId = Number(v.siteHierarchyId);
      const vtId = Number(vt.id);
      if (!Number.isNaN(candidateId) && !Number.isNaN(vtId) && candidateId === vtId) {
        return true;
      }
      if (vt.name && v.name && String(v.name).toLowerCase() === String(vt.name).toLowerCase()) {
        return true;
      }
      if (vt.virtualTenantIdentifier && v.name && String(v.name).toLowerCase() === String(vt.virtualTenantIdentifier).toLowerCase()) {
        return true;
      }
      return false;
    });

    if (vtContainer && Array.isArray(vtContainer.rules) && vtContainer.rules.length > 0) {
      const currentVirtualTenantId = String(vt.id);
      const mappedRules = vtContainer.rules.filter(rule => {
        const mappedVirtualTenantIds = ruleToVirtualTenantMapping.get(Number(rule.siteRuleId)) || [];
        return mappedVirtualTenantIds.length === 0 || mappedVirtualTenantIds.includes(currentVirtualTenantId);
      });
      // Legacy VT rules were stored as site rules and are only linked via the local mapping.
      const vtRuleIds = new Set(mappedRules.map(rule => Number(rule.siteRuleId)));
      const legacySiteRules = siteRules.filter(rule =>
        !vtRuleIds.has(Number(rule.siteRuleId))
        && (ruleToVirtualTenantMapping.get(Number(rule.siteRuleId)) || []).includes(currentVirtualTenantId)
      );
      return { parentRules: inheritedParentRules, vtRules: [...mappedRules, ...legacySiteRules] };
    }

    const vtPathIds = new Set(
      (vtPathItems || [])
        .map(p => Number(p.alarmPathId ?? p.id))
        .filter(n => !Number.isNaN(n) && n > 0)
    );

    if (vtPathIds.size === 0) {
      return { parentRules: siteRules, vtRules: [] };
    }

    const details = await Promise.allSettled(siteRules.map(r => getRule(r.siteRuleId)));
    const matchedRules: RuleSummary[] = [];
    details.forEach((result, idx) => {
      const candidateRule = siteRules[idx];
      const mappedVirtualTenantIds = ruleToVirtualTenantMapping.get(Number(candidateRule.siteRuleId)) || [];
      if (mappedVirtualTenantIds.length > 0) {
        if (mappedVirtualTenantIds.includes(String(vt.id))) {
          matchedRules.push(candidateRule);
        }
        return;
      }

      if (result.status !== 'fulfilled' || !result.value?.success || !result.value.data) {
        return;
      }
      const ruleDetail = result.value.data;
      const rulePathIds = new Set(
        (ruleDetail.alarmPaths || [])
          .map(p => Number(p.id))
          .filter(n => !Number.isNaN(n) && n > 0)
      );
      let hasOverlap = false;
      for (const id of rulePathIds) {
        if (vtPathIds.has(id)) {
          hasOverlap = true;
          break;
        }
      }
      if (hasOverlap) {
        matchedRules.push(candidateRule);
      }
    });

    return { parentRules: inheritedParentRules, vtRules: matchedRules };
  };

  const loadVirtualTenantDetails = async (vtIdFromRoute: number) => {
    setLoading(true);
    setError(null);
    setActionError(null);
    try {
      const vtRes = await listVirtualTenants();
      if (!vtRes?.success || !Array.isArray(vtRes.data)) {
        setError(vtRes?.error?.message || 'Failed to load virtual tenants');
        return;
      }

      const found = vtRes.data.find((vt: VirtualTenantDetail) => vt.id === vtIdFromRoute);
      if (!found) {
        setError('Virtual tenant not found');
        return;
      }

      setVirtualTenant(found);

      try {
        const parentSiteId = found.siteIdentifierRegistryId || found.parentId || found.parentSiteHierarchyId;
        if ((found.customerName === undefined || found.customerName === null || found.customerName === '') && parentSiteId) {
          const siteRes = await getSite(parentSiteId);
          if (siteRes?.success && siteRes.data) {
            setVirtualTenant(prev => prev ? ({ ...prev, customerId: (siteRes.data as any).customerId, customerName: (siteRes.data as any).customerName }) : prev);
          }
        }
      } catch (siteErr) {
        console.info('[VirtualTenantsDetails] Failed to fetch parent site for customer info', siteErr);
      }

      const pathsRes = await getVirtualTenantAlarmPaths(vtIdFromRoute);
      const vtPathItems = pathsRes?.success && Array.isArray(pathsRes.data)
        ? (pathsRes.data as unknown as AlarmPathAssociation[])
        : [];
      setAlarmPaths(vtPathItems);

      const resolvedRules = await resolveVirtualTenantRules(found, vtPathItems);
      setParentSiteRules(resolvedRules.parentRules);
      setVirtualTenantRules(resolvedRules.vtRules);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    const vtId = Number(String(id).replace('vt-', ''));
    if (Number.isNaN(vtId)) {
      return;
    }

    const handleRuleLifecycleEvent = () => {
      void loadVirtualTenantDetails(vtId);
    };

    const eventNames = ['rules:created', 'rules:updated', 'rules:deleted', 'rules:status-changed'];
    eventNames.forEach(eventName => {
      globalThis.addEventListener(eventName, handleRuleLifecycleEvent as EventListener);
    });

    return () => {
      eventNames.forEach(eventName => {
        globalThis.removeEventListener(eventName, handleRuleLifecycleEvent as EventListener);
      });
    };
  }, [id, loadVirtualTenantDetails]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const vtId = Number(String(id).replace('vt-', ''));
    if (Number.isNaN(vtId)) {
      return;
    }

    const handleRuleLifecycleEvent = () => {
      void loadVirtualTenantDetails(vtId);
    };

    const eventNames = ['rules:created', 'rules:updated', 'rules:deleted', 'rules:status-changed'];
    eventNames.forEach(eventName => {
      globalThis.addEventListener(eventName, handleRuleLifecycleEvent as EventListener);
    });

    return () => {
      eventNames.forEach(eventName => {
        globalThis.removeEventListener(eventName, handleRuleLifecycleEvent as EventListener);
      });
    };
  }, [id, loadVirtualTenantDetails]);

  const handleEditRule = async (ruleId: number) => {
    if (!virtualTenant) {
      return;
    }
    setSuccessMessage(null);
    setActionError(null);
    try {
      const res = await getRule(ruleId);
      if (res?.success && res.data) {
        const parentSiteRegistryId = virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId;
        const formData = mapRuleDetailToFormData(res.data, Number(parentSiteRegistryId));
        setEditingRuleId(ruleId);
        setEditingRuleData(formData);
        setIsEditRuleModalOpen(true);
      } else {
        setActionError(res?.error?.message || 'Failed to load rule details');
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleUpdateRule = async (ruleId: number, ruleData: RuleFormData) => {
    if (!isEditFormValid) {
      setShowEditErrorSummary(true);
      return;
    }
    setActionError(null);
    setSuccessMessage(null);
    setShowEditErrorSummary(false);

    try {
      const rulePathIds = resolveVirtualTenantRulePathIds(ruleData.filteringPaths);
      const payload = {
        ruleName: ruleData.ruleName || `Rule ${ruleId}`,
        isActive: !!ruleData.isActive,
        isUltraCritical: !!ruleData.isUltraCritical,
        bindPathPriority: !!ruleData.bindPathPriority,
        filteringCriteria: {
          pathIds: rulePathIds,
          paths: rulePathIds.map(n => String(n)),
          priorities: Array.isArray(ruleData.filteringPriorities) ? ruleData.filteringPriorities : [],
          classes: [],
          patterns: []
        },
        distributionGroupIds: (ruleData.distributionGroupIds || '').split(',').map(s => Number(s.trim())).filter(Boolean),
        attachmentGroupIds: (ruleData.attachmentGroupIds || '').split(',').map(s => Number(s.trim())).filter(Boolean),
        behavior: {
          hideRtn: !!ruleData.hideRtn,
          rtnExceptions: (ruleData.rtnExceptions || '').split(',').map(s => s.trim()).filter(Boolean)
        }
      };

      const res = await updateRule(ruleId, payload);
      if (res?.success) {
        if (virtualTenant?.id) {
          saveRuleToVirtualTenantMapping(ruleId, [String(virtualTenant.id)]);
        }
        setSuccessMessage('Rule updated successfully');
        setIsEditRuleModalOpen(false);
        setEditingRuleId(null);
        setEditingRuleData(null);

        const vtId = id ? Number(String(id).replace('vt-', '')) : NaN;
        if (!Number.isNaN(vtId)) {
          await loadVirtualTenantDetails(vtId);
        }
      } else {
        setActionError(res?.error?.message || 'Failed to update rule');
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    const targetRule = (virtualTenantRules || []).find(r => r.siteRuleId === ruleId);
    const confirmed = await showConfirmModal(
      targetRule?.isActive
        ? 'Are you sure you want to deactivate this rule?'
        : 'Are you sure you want to delete this rule? This action cannot be undone.',
      targetRule?.isActive ? 'Deactivate Rule' : 'Delete Rule'
    );
    if (!confirmed) {
      return;
    }

    setActionError(null);
    setSuccessMessage(null);

    try {
      let res;
      if (targetRule?.isActive) {
        res = await patchRuleStatus(ruleId, { isActive: false });
      } else {
        res = await deleteRule(ruleId);
      }

      if (res?.success) {
        if (!targetRule?.isActive) {
          deleteRuleToVirtualTenantMapping(ruleId);
        }
        setSuccessMessage(targetRule?.isActive ? 'Rule deactivated successfully' : 'Rule deleted successfully');
        const vtId = id ? Number(String(id).replace('vt-', '')) : NaN;
        if (!Number.isNaN(vtId)) {
          await loadVirtualTenantDetails(vtId);
        }
      } else {
        setActionError(res?.error?.message || 'Failed to deactivate/delete rule');
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleActivateRule = async (ruleId: number) => {
    const confirmed = await showConfirmModal('Are you sure you want to activate this rule?', 'Activate Rule');
    if (!confirmed) {
      return;
    }

    setActionError(null);
    setSuccessMessage(null);

    try {
      const res = await patchRuleStatus(ruleId, { isActive: true });
      if (res?.success) {
        setSuccessMessage('Rule activated successfully');
        const vtId = id ? Number(String(id).replace('vt-', '')) : NaN;
        if (!Number.isNaN(vtId)) {
          await loadVirtualTenantDetails(vtId);
        }
      } else {
        setActionError(res?.error?.message || 'Failed to activate rule');
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleCreateRule = async () => {
    if (!isCreateFormValid) {
      setShowCreateErrorSummary(true);
      return;
    }

    if (!virtualTenant) {
      setActionError('Cannot create rule: virtual tenant is not available.');
      return;
    }

    const ruleData = newRules[0];
    if (!ruleData) {
      setActionError('No rule to create');
      return;
    }

    const parentSiteRegistryId = virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId;
    if (!parentSiteRegistryId) {
      setActionError('Cannot create rule: parent site registry ID is not available.');
      return;
    }

    let siteHierarchyId: number | undefined;
    try {
      const rulesRes = await listSiteRules(parentSiteRegistryId);
      if (rulesRes?.success && rulesRes.data?.siteHierarchyId) {
        const vtContainer = (rulesRes.data.virtualTenants || [])
          .find(v => Number(v.siteHierarchyId) === Number(virtualTenant.id));
        siteHierarchyId = Number(vtContainer?.siteHierarchyId ?? virtualTenant.id);
      } else {
        setActionError('Cannot create rule: the virtual tenant site is not yet configured in the rules system.');
        return;
      }
    } catch (err: unknown) {
      setActionError(`Cannot create rule: failed to query site configuration.${err instanceof Error ? ` ${err.message}` : ''}`);
      return;
    }

    setActionError(null);
    setSuccessMessage(null);
    setShowCreateErrorSummary(false);

    const rulePathIds = resolveVirtualTenantRulePathIds(ruleData.filteringPaths);
    if (rulePathIds.length === 0) {
      setActionError('Cannot create rule: this virtual tenant has no associated alarm paths.');
      return;
    }

    const payload: CreateRuleRequest = {
      siteIdentifierRegistryId: Number(parentSiteRegistryId),
      siteHierarchyId,
      entityType: 'VirtualTenant',
      ruleName: ruleData.ruleName || `Rule for ${virtualTenant.name ?? virtualTenant.virtualTenantIdentifier ?? `VT ${virtualTenant.id}`}`,
      isActive: !!ruleData.isActive,
      isUltraCritical: !!ruleData.isUltraCritical,
      bindPathPriority: !!ruleData.bindPathPriority,
      filteringCriteria: {
        pathIds: rulePathIds,
        paths: rulePathIds.map(n => String(n)),
        priorities: Array.isArray(ruleData.filteringPriorities) ? ruleData.filteringPriorities : [],
        classes: [],
        patterns: []
      },
      distributionGroupIds: (ruleData.distributionGroupIds || '').split(',').map(s => Number(s.trim())).filter(Boolean),
      attachmentGroupIds: (ruleData.attachmentGroupIds || '').split(',').map(s => Number(s.trim())).filter(Boolean),
      behavior: {
        hideRtn: !!ruleData.hideRtn,
        rtnExceptions: (ruleData.rtnExceptions || '').split(',').map(s => s.trim()).filter(Boolean)
      }
    };

    try {
      const res = await createRule(payload);
      if (res?.success) {
        const createdRuleId = Number((res as { data?: { siteRuleId?: number } })?.data?.siteRuleId);
        if (!Number.isNaN(createdRuleId) && virtualTenant?.id) {
          saveRuleToVirtualTenantMapping(createdRuleId, [String(virtualTenant.id)]);
        }
        setSuccessMessage('Rule created successfully');
        setIsCreateModalOpen(false);
        setNewRules([]);

        if (virtualTenant?.id) {
          await loadVirtualTenantDetails(Number(virtualTenant.id));
        }
      } else {
        setActionError(res?.error?.message || 'Failed to create rule');
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    if (!id) return;
    const vtId = parseInt(id.replace('vt-', ''), 10);
    if (isNaN(vtId)) {
      setError('Invalid virtual tenant ID');
      return;
    }
    void loadVirtualTenantDetails(vtId);
  }, [id]);

  // Call useBreadcrumbs before any conditional returns (Rules of Hooks)
  const breadcrumbs = useBreadcrumbs({
    customerId: virtualTenant?.customerId,
    customerName: virtualTenant?.customerName,
    siteId: virtualTenant?.siteIdentifierRegistryId,
    siteName: virtualTenant?.parentName,
    virtualTenantId: virtualTenant ? `vt-${virtualTenant.id}` : undefined,
    virtualTenantName: virtualTenant?.name ?? virtualTenant?.virtualTenantIdentifier ?? (virtualTenant ? `VT ${virtualTenant.id}` : undefined),
  });

  if (loading || error) {
    return (
      <main>
        <div className="container">
          <section className='page'>
            <h2 className="heading__title">{loading ? 'Loading virtual tenant…' : 'Virtual tenant details'}</h2>
            <DateTimeDisplay />
          </section>
          <section className="page-section">
            {loading && <LoadingSpinner message="Loading virtual tenant…" />}
            {error && <div>Error loading virtual tenant: {error}</div>}
          </section>
        </div>
      </main>
    );
  }

  if (!virtualTenant) return (
    <main>
      <div className="container">
        <div className="page-section">
          <h2>Virtual Tenant Not Found</h2>
          <p>The requested virtual tenant could not be found.</p>
          <button className="btn btn--secondary" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>
    </main>
  );

  return (
    <main>
      <div className="container">
        

        <section className='page'>
          <div className="heading__section">
            <div className="heading__title-group">
              <h2 className="heading__title heading__title--virtual-tenant">{virtualTenant.name ?? virtualTenant.virtualTenantIdentifier ?? `VT ${virtualTenant.id}`}</h2>
              <DateTimeDisplay />
            </div>
            
            <button type="button" className="btn btn-edit" onClick={async () => {
              try {
                const pathsRes = await getVirtualTenantAlarmPaths(virtualTenant.id);
                let alarmPathIds: number[] = [];
                if (pathsRes?.success && Array.isArray(pathsRes.data)) {
                  alarmPathIds = pathsRes.data
                    .map((item: any) => (typeof item === 'number' ? item : (item.alarmPathId ?? item.id)))
                    .filter((item): item is number => typeof item === 'number');
                }
                setEditingVT({
                  ...virtualTenant,
                  alarmPathIds,
                });
                setIsEditModalOpen(true);
              } catch (e) {
                console.info('[VirtualTenantsDetails] Failed to fetch VT alarm paths:', e);
                setEditingVT(virtualTenant);
                setIsEditModalOpen(true);
              }
            }}>Edit Virtual Tenant</button>
          </div>
        </section>

        <Breadcrumbs items={breadcrumbs} />

        {successMessage && (
          <StatusMessage kind="success" onClose={() => setSuccessMessage(null)}>{successMessage}</StatusMessage>
        )}
        {actionError && (
          <StatusMessage kind="error" onClose={() => setActionError(null)}>{actionError}</StatusMessage>
        )}

        <section className="page-section mb-2">
          <h3 className='heading__title heading__title--info mt-0 mb-1'>Associated Alarm Paths</h3>
          {alarmPaths && alarmPaths.length > 0 ? (
            <table className="table-responsive">
              <thead>
                <tr>
                  <th>Path</th>
                  <th>Path ID</th>
                  <th>Site Identifier</th>
                  <th>Inherit from Parent</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {alarmPaths.map((path: AlarmPathAssociation) => (
                  <tr key={path.id}>
                    <td>{path.pathToAlarm ?? '—'}</td>
                    <td>{path.alarmPathId ?? '—'}</td>
                    <td>{path.siteIdentifier ?? '—'}</td>
                    <td>{path.inheritFromParent ? 'Yes' : 'No'}</td>
                    <td><span className="badge badge--priority">{path.priority ?? '—'}</span></td>
                    <td><span className={path.isActive ? 'badge badge--active' : 'badge badge--inactive'}>{path.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>{path.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No alarm paths associated with this virtual tenant{parentSiteRules && parentSiteRules.length > 0 ? ' (inheriting from parent site)' : ''}</p>
          )}
        </section>

        <section className="alarm-content mb-2">
          <div className="alarm-content--heading">
            <h3 className='heading heading__title--info'>Virtual Tenant Rules</h3>
            {virtualTenant && (
              <div>
                <button
                  type="button"
                  className="btn btn-add-new"
                  onClick={() => {
                    setNewRules([{
                      ruleName: '',
                      isActive: true,
                      isUltraCritical: false,
                      bindPathPriority: true,
                      distributionGroupIds: '',
                      attachmentGroupIds: '',
                      filteringPaths: '',
                      filteringPriorities: [],
                      hideRtn: false,
                      rtnExceptions: ''
                    }]);
                    setIsCreateModalOpen(true);
                  }}
                >
                  Add Rule
                </button>
              </div>
            )}
          </div>

          {virtualTenantRules && virtualTenantRules.length > 0 && (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Rule Name</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th>Bind Path Priority</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {virtualTenantRules.map((rule: RuleSummary) => (
                    <tr key={rule.siteRuleId}>
                      <td>{rule.ruleName}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={rule.isActive ? 'badge badge--active' : 'badge badge--inactive'}>{rule.isActive ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td>{rule.bindPathPriority ? 'Yes' : 'No'}</td>
                      <td>{formatDateTime(rule.createdAt)}</td>
                      <td>
                        <ActionList
                          customActions={[
                            { label: 'Edit', onClick: () => handleEditRule(rule.siteRuleId), className: 'actions__item--edit' },
                            ...(rule.isActive
                              ? [{ label: 'Deactivate', onClick: () => handleDeleteRule(rule.siteRuleId), className: 'actions__item--deactivate' }]
                              : [
                                { label: 'Activate', onClick: () => handleActivateRule(rule.siteRuleId), className: 'actions__item--acknowledge' },
                                { label: 'Delete', onClick: () => handleDeleteRule(rule.siteRuleId), className: 'actions__item--delete' }
                              ])
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!virtualTenantRules || virtualTenantRules.length === 0) && (
            <div className='pb-1'><p>No rules configured for this virtual tenant.</p></div>
          )}
        </section>

        {parentSiteRules && parentSiteRules.length > 0 && (!virtualTenantRules || virtualTenantRules.length === 0) && (
          <section className="page-section mb-2">
            <h3 className='heading__title heading__title--info mt-0 mb-1'>Inherited Rules from Parent Site</h3>
            <table className="table-responsive">
              <thead>
                <tr>
                  <th>Rule Name</th>
                  <th>Status</th>
                  <th>Bind Path Priority</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {parentSiteRules.map((rule: RuleSummary) => (
                  <tr key={rule.siteRuleId}>
                    <td>{rule.ruleName}</td>
                    <td>
                      <span className={rule.isActive ? 'badge badge--active' : 'badge badge--inactive'}>
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{rule.bindPathPriority ? 'Yes' : 'No'}</td>
                    <td>{formatDateTime(rule.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <section className="alarm-content mb-2 p-1">
          <h3 className="heading heading__title--virtual-tenant mt-0 mb-1">Virtual Tenant Information</h3>
          <div className="details-grid">
            <div><strong>Virtual Tenant ID</strong></div><div>{virtualTenant.id}</div>
            <div><strong>Name</strong></div><div>{virtualTenant.name}</div>
            <div><strong>Virtual Tenant Identifier</strong></div><div>{virtualTenant.virtualTenantIdentifier ?? '—'}</div>
          </div>
        </section>

        <section className="alarm-content mb-2 p-1">
          <h3 className='heading__title heading__title--info mt-0 mb-1'>System Details</h3>
          <div className="details-grid">
            <div><strong>Parent Site</strong></div><div>
              {virtualTenant.parentName ? (
                <Link
                  to={`/sites/${virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId}`}
                  className="alarm-name-link"
                >
                  {virtualTenant.parentName}
                </Link>
              ) : virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId ? (
                <Link
                  to={`/sites/${virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId}`}
                  className="alarm-name-link"
                >
                  Site {virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId}
                </Link>
              ) : (
                '—'
              )}
            </div>
            <div><strong>Customer</strong></div><div>{virtualTenant.customerId ? (
              <Link to={`/customers/customer-${virtualTenant.customerId}`} className="alarm-name-link">
                {virtualTenant.customerName ?? `Customer ${virtualTenant.customerId}`}
              </Link>
            ) : (
              (virtualTenant.customerName ?? '—')
            )}</div>
            <div><strong>Status</strong></div><div>{virtualTenant.status ?? (virtualTenant.isActive ? 'Active' : 'Inactive')}</div>
            <div><strong>Is Online</strong></div><div>{virtualTenant.isOnline ? 'Yes' : 'No'}</div>
            <div><strong>Active Rules</strong></div><div>{virtualTenant.ruleCount ?? virtualTenant.vtStatistics?.activeRules ?? 0}</div>
          </div>
        </section>

        <section className="alarm-content mb-2 p-1">
          <h3 className='heading__title heading__title--info mt-0 mb-1'>Additional Information</h3>
          <div className="details-grid">
            <div><strong>Created</strong></div><div>{formatDateTime(virtualTenant.createdAt)}</div>
            <div><strong>Last Heartbeat</strong></div><div>{formatDateTime(virtualTenant.lastHeartbeat) ?? '—'}</div>
            <div><strong>Associated Alarm Paths</strong></div><div>{alarmPaths ? alarmPaths.length : '—'}</div>
          </div>
        </section>
      </div>

      <Modal
        isOpen={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingVT(null);
        }}
        onConfirm={async () => {
          const form = document.querySelector('[data-vt-edit-form]') as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          }
        }}
        title="Edit Virtual Tenant"
        content={
          editingVT && virtualTenant && (
            <>
              <h3 className='heading__title heading__title--info mt-1 mb-1'>Virtual Tenant Configuration</h3>
              <VirtualTenantsForm
                siteId={String(virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId || '')}
                parentSiteId={Number(virtualTenant.siteIdentifierRegistryId || virtualTenant.parentId || virtualTenant.parentSiteHierarchyId)}
                initialItems={[virtualTenant]}
                isModal={true}
                isEdit={true}
                editingItem={editingVT}
                onUpdated={async () => {
                  setIsEditModalOpen(false);
                  setEditingVT(null);
                  await new Promise(resolve => setTimeout(resolve, 200));
                  try {
                    const vtRes = await listVirtualTenants();
                    if (vtRes?.success && Array.isArray(vtRes.data)) {
                      const found = vtRes.data.find((vt: VirtualTenantDetail) => vt.id === virtualTenant.id);
                      if (found) {
                        setVirtualTenant(found);
                        const pathsRes = await getVirtualTenantAlarmPaths(found.id);
                        if (pathsRes?.success && Array.isArray(pathsRes.data)) {
                          setAlarmPaths(pathsRes.data as unknown as AlarmPathAssociation[]);
                        }
                      }
                    }
                  } catch (e: any) {
                    console.info('[VirtualTenantsDetails] Failed to refresh after update:', e);
                  }
                }}
                onDeleted={async () => {}}
              />
            </>
          )
        }
      />

      <Modal
        isOpen={isEditRuleModalOpen}
        onCancel={() => {
          setIsEditRuleModalOpen(false);
          setEditingRuleId(null);
          setEditingRuleData(null);
          setShowEditErrorSummary(false);
        }}
        onConfirm={() => {
          if (editingRuleId && editingRuleData) {
            return handleUpdateRule(editingRuleId, editingRuleData);
          }
        }}
        title="Edit Rule"
        content={
          editingRuleData && (
            <>
              <h3 className='heading__title heading__title--info mt-1 mb-1'>Rule Configuration</h3>
              <RulesForm
                value={[editingRuleData]}
                onChange={data => setEditingRuleData(data[0] || null)}
                disabled={false}
                siteId={virtualTenant?.siteIdentifierRegistryId || virtualTenant?.parentId || virtualTenant?.parentSiteHierarchyId || undefined}
                hideBuildingSelector={true}
                hideAddRuleButton={true}
                hideVirtualTenantSelector={true}
                associatedAlarmPaths={alarmPaths ?? undefined}
                onValidate={isValid => setIsEditFormValid(isValid)}
                showErrorSummary={showEditErrorSummary}
                onCloseErrorSummary={() => setShowEditErrorSummary(false)}
              />
            </>
          )
        }
      />

      <Modal
        isOpen={isCreateModalOpen}
        onCancel={() => {
          setIsCreateModalOpen(false);
          setNewRules([]);
          setShowCreateErrorSummary(false);
        }}
        onConfirm={handleCreateRule}
        title="Create New Rule"
        content={
          <>
            <h3 className='heading__title heading__title--info mt-1 mb-1'>Rule Configuration</h3>
            <RulesForm
              value={newRules}
              onChange={setNewRules}
              disabled={false}
              siteId={virtualTenant?.siteIdentifierRegistryId || virtualTenant?.parentId || virtualTenant?.parentSiteHierarchyId || undefined}
              hideBuildingSelector={true}
              hideVirtualTenantSelector={true}
              hideAddRuleButton={true}
              associatedAlarmPaths={alarmPaths ?? undefined}
              onValidate={isValid => setIsCreateFormValid(isValid)}
              showErrorSummary={showCreateErrorSummary}
              onCloseErrorSummary={() => setShowCreateErrorSummary(false)}
            />
          </>
        }
      />

      <Modal
        isOpen={confirmModalOpen}
        title={confirmModalTitle}
        headerClass={confirmModalHeaderClass}
        content={<ModalContent heading={confirmModalTitle} message={confirmModalMessage} />}
        onConfirm={() => {
          setConfirmModalOpen(false);
          confirmResolverRef.current?.(true);
        }}
        onCancel={() => {
          setConfirmModalOpen(false);
          confirmResolverRef.current?.(false);
        }}
      />
    </main>
  );
};

export default VirtualTenantsDetails;
